/**
 * @file DiagnosticService.js
 * @description Servicio para manejo de diagnósticos y monitoreo del sistema
 * Implementa el principio de responsabilidad única separando la lógica de diagnóstico de los componentes UI
 */

/**
 * Clase que proporciona servicios de diagnóstico para diferentes subsistemas
 * Sigue el patrón Singleton para garantizar un único punto de acceso a los diagnósticos
 */
class DiagnosticService {
  constructor() {
    this.diagnosticDataSources = {
      cors: {
        retryLogsKey: 'nubelytics_cors_retries',
        proxyCallsKey: 'nubelytics_proxy_calls',
        statsKey: 'nubelytics_cors_proxy_stats'
      }
    };
    
    this.endpoints = {
      api: 'api.nubelytics.com',
      sync: 'sync.nubelytics.com'
    };
  }
  
  /**
   * Obtiene los datos de diagnóstico del sistema CORS
   * @returns {Object} Datos de diagnóstico CORS
   */
  getCorsProxyDiagnostics() {
    try {
      const keys = this.diagnosticDataSources.cors;
      return {
        retryLogs: JSON.parse(localStorage.getItem(keys.retryLogsKey) || '[]'),
        proxyCalls: JSON.parse(localStorage.getItem(keys.proxyCallsKey) || '[]'),
        proxyStats: JSON.parse(localStorage.getItem(keys.statsKey) || '{}')
      };
    } catch (error) {
      console.error('Error al recuperar diagnósticos CORS:', error);
      return {
        error: error.message,
        retryLogs: [],
        proxyCalls: [],
        proxyStats: {}
      };
    }
  }
  
  /**
   * Limpia los datos de diagnóstico de CORS
   * @returns {boolean} True si se completó correctamente
   */
  clearCorsDiagnostics() {
    try {
      const keys = this.diagnosticDataSources.cors;
      Object.values(keys).forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.error('Error al limpiar diagnósticos:', error);
      return false;
    }
  }
  
  /**
   * Verifica el estado de un servidor mediante una llamada de healthcheck
   * @param {string} serverDomain - Dominio del servidor a verificar
   * @param {Function} updateCallback - Función a llamar con el resultado
   */
  async checkServerStatus(serverDomain, updateCallback = null) {
    try {
      // Usar nuestro proxy para prevenir errores CORS durante diagnóstico
      let functionBaseUrl = process.env.REACT_APP_FUNCTIONS_BASE_URL || '/proxy';

      // Si estamos en Firebase Hosting y la config inyectada está disponible, ajustar automáticamente
      if (typeof window !== 'undefined' && window.__FIREBASE_CONFIG__ && !process.env.REACT_APP_FUNCTIONS_BASE_URL) {
        try {
          const { projectId = 'nubelytics', region = 'us-central1' } = window.__FIREBASE_CONFIG__;
          functionBaseUrl = `https://${region}-${projectId}.cloudfunctions.net`;
        } catch (e) {
          console.warn('[DiagnosticService] No se pudo leer __FIREBASE_CONFIG__:', e);
        }
      }

      // Determinar prefijo correcto: si la base ya contiene el nombre de la función, no agregar /corsProxy
      let proxyPrefix;
      if (functionBaseUrl === '/proxy') {
        proxyPrefix = '/proxy?url=';
      } else {
        const baseLower = functionBaseUrl.toLowerCase();
        proxyPrefix = baseLower.includes('run.app') || baseLower.includes('corsproxy')
          ? `${functionBaseUrl}?url=`
          : `${functionBaseUrl}/corsProxy?url=`;
      }

      const encodedUrl = encodeURIComponent(`https://${serverDomain}/healthcheck`);
      const proxyUrl = `${proxyPrefix}${encodedUrl}`;

      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'X-Diagnostic-Check': 'true'
        }
      });
      
      const status = response.ok ? 'online' : 'offline';
      
      if (updateCallback && typeof updateCallback === 'function') {
        updateCallback(serverDomain, status);
      }
      
      return status;
    } catch (error) {
      console.warn(`Error al verificar ${serverDomain}:`, error);
      
      if (updateCallback && typeof updateCallback === 'function') {
        updateCallback(serverDomain, 'error');
      }
      
      return 'error';
    }
  }
  
  /**
   * Verifica el estado de todos los servidores configurados
   * @param {Function} updateCallback - Callback para actualizar UI por cada servidor
   * @returns {Promise<Object>} Estado de cada servidor
   */
  async checkAllServers(updateCallback = null) {
    const results = {};
    
    for (const [key, domain] of Object.entries(this.endpoints)) {
      results[key] = await this.checkServerStatus(domain, updateCallback);
    }
    
    return results;
  }
  
  /**
   * Registra un evento importante en el sistema
   * @param {string} eventType - Tipo de evento
   * @param {Object} data - Datos asociados al evento
   */
  logSystemEvent(eventType, data) {
    try {
      const events = JSON.parse(localStorage.getItem('nubelytics_system_events') || '[]');
      
      events.unshift({
        type: eventType,
        timestamp: new Date().toISOString(),
        data
      });
      
      // Limitar a 100 eventos para no sobrecargar localStorage
      if (events.length > 100) {
        events.length = 100;
      }
      
      localStorage.setItem('nubelytics_system_events', JSON.stringify(events));
    } catch (error) {
      console.error('Error al registrar evento:', error);
    }
  }
}

// Exportar como singleton
const diagnosticService = new DiagnosticService();
export default diagnosticService;
